"use client";

import React from "react";
import {
  CardConditionEnum,
  FinishEnum,
  NONFOIL_FINISH,
} from "modules/cards/model/card";
import {
  cardConditionStrings,
  cardConditionStringsFull,
} from "modules/common/model/models";
import FoilIndicator from "modules/common/components/Table/FoilIndicator";
import LanguageFlag from "modules/common/components/Table/LanguageFlag";
import { cn } from "utils/utils";
import { finishSpec } from "modules/cards/model/finishBehavior";

const conditionFillClasses: Record<CardConditionEnum, string> = {
  [CardConditionEnum._]: "bg-base-300 text-base-content",
  [CardConditionEnum.Poor]: "bg-card-condition-poor text-white",
  [CardConditionEnum.Played]: "bg-card-condition-played text-black",
  [CardConditionEnum.LightPlayed]: "bg-card-condition-light-played text-black",
  [CardConditionEnum.Good]: "bg-card-condition-good text-black",
  [CardConditionEnum.Excellent]: "bg-card-condition-excellent text-black",
  [CardConditionEnum.NearMint]: "bg-card-condition-near-mint text-black",
  [CardConditionEnum.Mint]: "bg-card-condition-mint text-black",
};

function storedCondition(
  condition: CardConditionEnum | number | undefined
): CardConditionEnum | null {
  if (
    typeof condition !== "number" ||
    !Number.isInteger(condition) ||
    condition <= CardConditionEnum._ ||
    condition > CardConditionEnum.Mint
  ) {
    return null;
  }
  return condition as CardConditionEnum;
}

interface CardSpecChipProps {
  condition?: CardConditionEnum | number;
  language?: string;
  quantity?: number | string;
  value: React.ReactNode;
  showMinimumCondition?: boolean;
}

/**
 * Shared card-art overlay used by visual trade items and drop pool cards.
 * Metadata segments are omitted when the source record does not carry them;
 * this component never invents a language, condition, or quantity.
 */
export function CardSpecChip({
  condition,
  language,
  quantity,
  value,
  showMinimumCondition = false,
}: CardSpecChipProps) {
  const exactCondition = storedCondition(condition);
  const conditionCode =
    exactCondition === null ? "" : cardConditionStrings[exactCondition];
  const conditionName =
    exactCondition === null ? "" : cardConditionStringsFull[exactCondition];
  const showMinimum =
    showMinimumCondition && exactCondition !== CardConditionEnum.Mint;
  const normalizedLanguage = language?.trim() ?? "";
  const numericQuantity = Number(quantity);
  const hasQuantity = Number.isFinite(numericQuantity) && numericQuantity > 1;

  const segments: React.ReactNode[] = [];

  if (exactCondition !== null && conditionCode && conditionName) {
    segments.push(
      <span
        key="condition"
        className={cn(
          "tooltip tooltip-secondary inline-flex items-center self-stretch px-2 font-bold leading-none",
          conditionFillClasses[exactCondition]
        )}
        data-tip={showMinimum ? `Minimum ${conditionName}` : conditionName}
      >
        {showMinimum && <span className="mr-0.5">≥</span>}
        {conditionCode}
      </span>
    );
  }

  if (hasQuantity) {
    segments.push(
      <span
        key="quantity"
        className="inline-flex items-center px-2 font-semibold"
      >
        ×{numericQuantity}
      </span>
    );
  }

  segments.push(
    <span
      key="value"
      className="inline-flex items-center gap-1 px-2 font-semibold"
    >
      {value}
    </span>
  );

  if (normalizedLanguage) {
    segments.push(
      <span key="language" className="inline-flex items-center px-2">
        <LanguageFlag language={normalizedLanguage} size="sm" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "badge badge-sm md:badge-md badge-soft border-base-content/20 overflow-visible",
        "ring-base-content/30 ring-1",
        "bottom-4.5 absolute left-0 right-0 z-10 mx-auto inline-flex w-fit lg:bottom-5",
        "items-stretch gap-0 border p-0 shadow-lg",
        "[&>span:first-child]:rounded-l-full [&>span:last-child]:rounded-r-full"
      )}
    >
      {segments.map((segment, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span
              aria-hidden
              className="bg-base-content/20 w-px self-stretch"
            />
          )}
          {segment}
        </React.Fragment>
      ))}
    </span>
  );
}

export function CardFinishBadge({
  finish,
  className,
}: {
  finish?: string;
  className?: string;
}) {
  const spec = finish ? finishSpec(finish) : undefined;
  if (!spec || finish === NONFOIL_FINISH) {
    return null;
  }

  return (
    <span
      className={cn(
        "badge badge-soft border-base-content/10 absolute right-2 top-[12%]",
        "z-[5] gap-1 border font-medium shadow-md",
        className
      )}
    >
      {spec.foilVisual && <FoilIndicator size={12} />}
      <span>{spec.label}</span>
    </span>
  );
}

export function CardFinishIndicator({ finish }: { finish?: string }) {
  const spec = finish ? finishSpec(finish) : undefined;
  if (!spec || finish === NONFOIL_FINISH) return null;

  if (spec.foilVisual) {
    return <FoilIndicator size={12} />;
  }

  return (
    <span className="badge badge-xs badge-outline whitespace-nowrap">
      {spec.label}
    </span>
  );
}
